import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FormikProps } from 'formik'
import * as Yup from 'yup'

import Form from '../../../../../shared/components/Form/Form.component'
import Modal from '../../../../../shared/components/Modal/Modal.component'

import env from '../../../../../networkRequests/apiClient/env.config'
import {
  createDiscountCodeForAdmin,
  resetCreateDiscountCodeForAdmin
} from '../../../../../redux/states/admin/payments/discountCodes/createDiscountCodeForAdmin/createDiscountCodeForAdmin.slice'
import { CreateDiscountCodeForAdminRequestPayload } from '../../../../../redux/states/admin/payments/discountCodes/createDiscountCodeForAdmin/createDiscountCodeForAdmin.types'
import { getDiscountCodesForAdmin } from '../../../../../redux/states/admin/payments/discountCodes/getDiscountCodesForAdmin/getDiscountCodesForAdmin.slice'
import {
  DiscountCode,
  GetDiscountCodesForAdminRequestPayload
} from '../../../../../redux/states/admin/payments/discountCodes/getDiscountCodesForAdmin/getDiscountCodesForAdmin.types'
import { RootState } from '../../../../../redux/store/store.types'
import { FormField } from '../../../../../shared/components/Form/Form.types'
import Snackbar from '../../../../../shared/components/Snackbar/Snackbar.functions'
import date from '../../../../../shared/functions/Date/date.functions'
import { useAppDispatch, useAppSelector } from '../../../../../shared/hooks/redux.hooks'
import { CreateDiscountCodeFormValues } from '../DiscountCodes.types'

const createValidationSchema = (existingCodes: DiscountCode[]) =>
  Yup.object({
    code: Yup.string()
      .required('Discount code is required')
      .min(3, 'Discount code must be at least 3 characters')
      .max(20, 'Discount code must be at most 20 characters')
      .matches(/^[A-Z0-9_-]+$/, 'Discount code can only contain uppercase letters, numbers, hyphens, and underscores')
      .test('duplicate-code-validation', 'Discount code already exists', function (value) {
        if (!value) return true

        return !existingCodes.some(
          (existingCode: DiscountCode) => existingCode.code.toUpperCase() === value.toUpperCase()
        )
      }),
    discountType: Yup.string()
      .required('Discount type is required')
      .oneOf(['percentage', 'fixed'], 'Invalid discount type'),
    discountValue: Yup.number()
      .required('Discount value is required')
      .positive('Discount value must be positive')
      .test('discount-value-validation', 'Invalid discount value', function (value) {
        const { discountType } = this.parent
        if (discountType === 'percentage') {
          return value <= 100
        }

        return true
      }),
    expiryDate: Yup.string()
      .required('Expiry date is required')
      .test('expiry-date-validation', 'Expiry date must be on or after today', function (value) {
        if (!value) return true

        // Get today's date at midnight for proper date comparison
        const todayAtMidnight = date().startOf('day')

        // Get the selected date at midnight
        const selectedDateAtMidnight = date(value).startOf('day')

        return selectedDateAtMidnight >= todayAtMidnight
      }),
    description: Yup.string().max(500, 'Description must be at most 500 characters')
  })

export const useDiscountCodeModal = (onSuccess: () => void) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useAppDispatch()

  // Reset form validation state when modal opens
  useEffect(() => {
    setIsFormValid(false)
  }, [])

  const {
    loading: createDiscountCodeLoading,
    success: createDiscountCodeSuccess,
    error: createDiscountCodeError
  } = useAppSelector((state: RootState) => state.createDiscountCodeForAdmin)

  const { success: discountCodesSuccess } = useAppSelector((state: RootState) => state.getDiscountCodesForAdmin)

  const initialValues: CreateDiscountCodeFormValues = useMemo(
    () => ({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      expiryDate: '',
      description: ''
    }),
    []
  )

  const handleFormChange = useCallback((formik: Partial<FormikProps<unknown>>) => {
    // Track form validation state
    if (formik.isValid !== undefined) {
      const hasErrors = formik.errors && Object.keys(formik.errors).length > 0
      const isValid = formik.isValid && !hasErrors
      setIsFormValid(isValid)
    }
  }, [])

  // Fetch existing discount codes for duplicate validation
  const fetchExistingCodes = useCallback(() => {
    const payload: GetDiscountCodesForAdminRequestPayload = {
      request: {
        page: 1,
        limit: 100, // Use maximum allowed limit
        search: '',
        status: ''
      }
    }
    dispatch(getDiscountCodesForAdmin(payload))
  }, [dispatch])

  // Get existing codes for validation
  const existingCodes = useMemo(() => {
    return discountCodesSuccess?.data?.records || []
  }, [discountCodesSuccess])

  const handleSubmit = useCallback(
    (values: unknown) => {
      const formValues = values as CreateDiscountCodeFormValues

      // Convert date to ISO string and ensure amount is a number
      const expiryDate = new Date(formValues.expiryDate)
      const isoExpiryDate = expiryDate.toISOString()

      // Map form values to API request format
      const request = {
        code: formValues.code.toUpperCase(),
        description: formValues.description || undefined,
        amount: Number(formValues.discountValue), // Ensure it's a number
        currency: env.STRIPE_MAIN_CURRENCY,
        type: formValues.discountType,
        appliesTo: ['offerRequest', 'trip'] as ('offerRequest' | 'trip')[], // Default to both
        priceRangeMin: undefined,
        priceRangeMax: undefined,
        expiresAt: isoExpiryDate // Convert to ISO datetime string
      }

      const payload: CreateDiscountCodeForAdminRequestPayload = {
        request,
        onSuccess: () => {
          // This will be called after successful creation
        }
      }

      dispatch(createDiscountCodeForAdmin(payload))
    },
    [dispatch]
  )

  // Handle success response
  useEffect(() => {
    if (createDiscountCodeSuccess) {
      setIsModalOpen(false)
      Snackbar.show({
        message: 'Discount code created successfully',
        severity: 'success'
      })

      // Call onSuccess callback
      onSuccess()

      // Reset the create discount code state to prevent re-triggering
      dispatch(resetCreateDiscountCodeForAdmin())
    }
  }, [createDiscountCodeSuccess, dispatch, onSuccess])

  // Handle error response
  useEffect(() => {
    if (createDiscountCodeError) {
      Snackbar.show({
        message: createDiscountCodeError.userFriendlyMessage || 'Failed to create discount code',
        severity: 'error'
      })
    }
  }, [createDiscountCodeError])

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: 'code',
        label: 'Discount Code',
        type: 'text',
        placeholder: 'SUMMER2025',
        grid: { xs: 12 },
        required: true,
        fullWidth: true,
        autoFocus: true
      },
      {
        name: 'discountType',
        label: 'Discount Type',
        type: 'select',
        options: [
          { value: 'percentage', label: 'Percentage (%)' },
          { value: 'fixed', label: 'Fixed Amount ($)' }
        ],
        grid: { xs: 12, sm: 6 },
        required: true,
        fullWidth: true
      },
      {
        name: 'discountValue',
        label: 'Discount Value',
        type: 'number',
        placeholder: '10',
        grid: { xs: 12, sm: 6 },
        required: true,
        fullWidth: true
      },
      {
        name: 'expiryDate',
        label: 'Expiry Date',
        type: 'date',
        grid: { xs: 12 },
        required: true,
        fullWidth: true
      },
      {
        name: 'description',
        label: 'Description (Optional)',
        type: 'textarea',
        placeholder: 'Enter a description for this discount code...',
        grid: { xs: 12 },
        required: false,
        fullWidth: true,
        rows: 3
      }
    ],
    []
  )

  const modalBody = useMemo(
    () => (
      <Form
        hideButtons
        initialValues={initialValues}
        validationSchema={createValidationSchema(existingCodes)}
        onSubmit={handleSubmit}
        onFormChange={handleFormChange}
        formFields={formFields}
        formRef={formRef}
      />
    ),
    [existingCodes, initialValues, handleSubmit, handleFormChange, formFields, formRef]
  )

  const showModal = useCallback(() => {
    // Fetch existing codes for duplicate validation
    fetchExistingCodes()
    setIsModalOpen(true)
  }, [fetchExistingCodes])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const modalComponent = (
    <Modal
      open={isModalOpen}
      onClose={closeModal}
      title="Create New Discount Code"
      subtitle="Fill in the details below to create a new discount code"
      body={modalBody}
      primaryButton={{
        label: 'Create Discount Code',
        onClick: () => {
          formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
        },
        disabled: createDiscountCodeLoading || !isFormValid,
        loading: createDiscountCodeLoading
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: closeModal
      }}
    />
  )

  return { showModal, modalComponent }
}

export default useDiscountCodeModal
